import React from "react";
import { useVoice } from "../../contexts/VoiceContext";
import { ConditionalVoiceButton } from "./conditional-voice-button";

interface StoryTextProps {
    children: React.ReactNode;
    showVoiceButton?: boolean;
    voiceButtonVariant?: "default" | "ghost" | "outline";
}

export function StoryText({ children, showVoiceButton = true, voiceButtonVariant = "ghost" }: StoryTextProps) {
    const textContent = typeof children === "string" ? children : extractTextFromReactNode(children);
    const { state } = useVoice();

    const isCurrentlyReading = state.currentText === textContent && (state.isPlaying || state.isPaused);

    return (
        <div className="space-y-2">
            <div
                className={`flex items-start gap-1 rounded-lg transition-all duration-300 ${
                    isCurrentlyReading ? "bg-primary/5 border-l-4 border-primary pl-4 py-2 shadow-sm" : ""
                }`}
                data-voice-text={textContent}
            >
                <p className="text-lg leading-relaxed text-foreground flex-1">{children}</p>
                {showVoiceButton && textContent && (
                    <ConditionalVoiceButton
                        text={textContent}
                        variant={voiceButtonVariant}
                        className="flex-shrink-0 mt-1"
                    />
                )}
            </div>
        </div>
    );
}

function extractTextFromReactNode(node: React.ReactNode): string {
    if (typeof node === "string") {
        return node;
    }

    if (typeof node === "number") {
        return node.toString();
    }

    if (React.isValidElement(node)) {
        const children = (node.props as { children?: React.ReactNode }).children;
        if (typeof children === "string") {
            return children;
        }
        if (Array.isArray(children)) {
            return children.map(extractTextFromReactNode).join(" ");
        }
        return extractTextFromReactNode(children);
    }

    if (Array.isArray(node)) {
        return node.map(extractTextFromReactNode).join(" ");
    }

    return "";
}
